'use client';

/* eslint-disable no-param-reassign -- required by @tanstack/react-db draft pattern */

import { createId } from '@paralleldrive/cuid2';
import { and, eq } from '@tanstack/db';
import { useLiveQuery } from '@tanstack/react-db';
import { useCallback, useEffect, useState } from 'react';
import type { CreateItemInput, LibraryGroup, LibraryItem, SavedItemStatus } from './schemas';
import { useLibraryCollections, useLibraryContext } from '@/contexts/LibraryContext';
import { normalizeGroupIds, getPrimaryGroupId, removeGroupId } from '@/lib/library/helpers';
import { showDeletedToast } from '@/lib/library/toasts';

export function useSavedItem(
  type: 'REGULATION' | 'PASAL',
  regulationId: string,
  articleNumber?: string
) {
  const collections = useLibraryCollections();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const { data, isLoading, isReady } = useLiveQuery((q) => {
    if (!collections || !mounted) return undefined;

    return q
      .from({ item: collections.itemCollection })
      .where(({ item }) => {
        if (type === 'PASAL' && articleNumber) {
          return and(eq(item.type, type), eq(item.detail.articleNumber, articleNumber));
        }
        return eq(item.type, type);
      });
  }, [collections, type, articleNumber, mounted]);

  const savedItem = data?.find(
    (item) =>
      item.detail.regulationId === regulationId &&
      (type !== 'PASAL' || item.detail.articleNumber === articleNumber)
  );

  return {
    savedItem,
    isSaved: !!savedItem,
    isLoading: !mounted || isLoading,
    isReady: mounted && isReady,
  };
}

export function useLibraryGroups() {
  const collections = useLibraryCollections();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const { data, isLoading, isReady } = useLiveQuery((q) => {
    if (!collections || !mounted) return undefined;
    return q.from({ group: collections.groupCollection });
  }, [collections, mounted]);

  return {
    groups: data ?? [],
    isLoading: !mounted || isLoading,
    isReady: mounted && isReady,
  };
}

export function useItemsByRegulation(regulationId: string) {
  const collections = useLibraryCollections();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const { data, isLoading, isReady } = useLiveQuery((q) => {
    if (!collections || !mounted) return undefined;
    return q.from({ item: collections.itemCollection });
  }, [collections, mounted]);

  const items = data?.filter((item) => item.detail.regulationId === regulationId) ?? [];

  return { items, isLoading: !mounted || isLoading, isReady: mounted && isReady };
}

export function useSavedStatus(
  items: LibraryItem[],
  type: 'REGULATION' | 'PASAL',
  articleNumber?: string
): SavedItemStatus {
  const match = items.find((item) => {
    if (item.type !== type) return false;
    if (type === 'PASAL') return item.detail?.articleNumber === articleNumber;
    return true;
  });

  if (match) {
    return {
      isSaved: true,
      libraryItemId: match.libraryItemId,
      libraryGroupId: match.libraryGroupId,
    };
  }
  return { isSaved: false };
}

export function useLibraryActions() {
  const { collections, userId } = useLibraryContext();

  const saveItem = useCallback(
    (input: CreateItemInput): LibraryItem | undefined => {
      if (!collections || !userId) return;

      const libraryItemId = createId();
      const detail = { ...input.detail };
      const groupIds = normalizeGroupIds(input.groupIds);
      if (groupIds.length > 0) {
        detail.groupIds = groupIds;
      }
      if (input.note !== undefined) {
        detail.note = input.note;
      }
      const primaryGroupId = groupIds[0] ?? input.libraryGroupId ?? null;

      const item: LibraryItem = {
        libraryItemId,
        userId,
        libraryGroupId: primaryGroupId,
        type: input.type,
        title: input.title,
        detail,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      collections.itemCollection.insert(item);
      return item;
    },
    [collections, userId]
  );

  const deleteItem = useCallback(
    (item: LibraryItem) => {
      if (!collections) return;

      collections.itemCollection.delete(item.libraryItemId);

      showDeletedToast(item.title, () => {
        if (!collections) return;
        collections.itemCollection.insert(item);
      });
    },
    [collections]
  );

  const updateItemGroups = useCallback(
    (itemId: string, groupIds: string[]) => {
      if (!collections) return;
      const normalized = normalizeGroupIds(groupIds);
      const primaryGroupId = getPrimaryGroupId(normalized);
      collections.itemCollection.update(itemId, (draft) => {
        draft.libraryGroupId = primaryGroupId;
        draft.detail = {
          ...draft.detail,
          ...(normalized.length > 0 ? { groupIds: normalized } : { groupIds: undefined }),
        };
        draft.updatedAt = new Date().toISOString();
      });
    },
    [collections]
  );

  const removeItemFromGroup = useCallback(
    (item: LibraryItem, groupId: string) => {
      if (!collections) return;
      const result = removeGroupId(item, groupId);
      if (result === null) {
        deleteItem(item);
      } else {
        collections.itemCollection.update(item.libraryItemId, (draft) => {
          draft.libraryGroupId = result.libraryGroupId;
          draft.detail = result.detail;
          draft.updatedAt = new Date().toISOString();
        });
      }
    },
    [collections, deleteItem]
  );

  const updateNote = useCallback(
    (itemId: string, note: string | null) => {
      if (!collections) return;
      collections.itemCollection.update(itemId, (draft) => {
        draft.detail = {
          ...draft.detail,
          ...(note !== null ? { note } : { note: undefined }),
        };
        draft.updatedAt = new Date().toISOString();
      });
    },
    [collections]
  );

  const moveToGroup = useCallback(
    (itemId: string, groupId: string | null) => {
      if (!collections) return;
      collections.itemCollection.update(itemId, (draft) => {
        draft.libraryGroupId = groupId;
      });
    },
    [collections]
  );

  const createGroup = useCallback(
    (name: string, description?: string) => {
      if (!collections || !userId) return;

      const libraryGroupId = createId();

      collections.groupCollection.insert({
        libraryGroupId,
        userId,
        name,
        description: description ?? null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    },
    [collections, userId]
  );

  const updateGroup = useCallback(
    (libraryGroupId: string, updates: { name?: string; description?: string | null }) => {
      if (!collections) return;
      collections.groupCollection.update(libraryGroupId, (draft) => {
        if (updates.name !== undefined) draft.name = updates.name;
        if (updates.description !== undefined) draft.description = updates.description;
        draft.updatedAt = new Date().toISOString();
      });
    },
    [collections]
  );

  const deleteGroup = useCallback(
    (group: LibraryGroup) => {
      if (!collections) return;
      collections.groupCollection.delete(group.libraryGroupId);
    },
    [collections]
  );

  return {
    saveItem,
    updateItemGroups,
    removeItemFromGroup,
    updateNote,
    deleteItem,
    moveToGroup,
    createGroup,
    updateGroup,
    deleteGroup,
  };
}
